/**
 * Answer style — how the voter likes VoteMate to write: the wording it uses and
 * how much it writes. Two independent communication preferences the voter picks;
 * never a reading level, and nothing here is inferred from what a voter writes.
 *
 * Wording runs Everyday words (`plain`) · Auto (`auto`, the default) ·
 * Policy-focused (`technical`), listed in that order so the default sits in the
 * middle, as Balanced does for length.
 *
 * The backend owns the vocabulary (`votemate-backend/config/answer_style.py`)
 * and the instructions each value maps to (`maf/prompts/answer_style.py`); the
 * client only ever sends these enum values.
 *
 * Kept byte-identical between the web and mobile clients, like `AnswerDepth.ts`.
 */
export const ANSWER_WORDINGS = ['plain', 'auto', 'technical'] as const;
export const ANSWER_LENGTHS = ['brief', 'balanced', 'thorough'] as const;

export type AnswerWording = (typeof ANSWER_WORDINGS)[number];
export type AnswerLength = (typeof ANSWER_LENGTHS)[number];

export interface AnswerStyle {
  wording: AnswerWording;
  length: AnswerLength;
}

export const DEFAULT_ANSWER_STYLE: AnswerStyle = { wording: 'auto', length: 'balanced' };

/**
 * Wording values from before Auto existed. `everyday` was the default then and
 * added no instruction, so it reads as Auto: a guest record saved by an older
 * build, and the account value the server still returns for Auto so older
 * builds can parse it (see `config/answer_style.py`).
 */
const LEGACY_WORDINGS = new Map<unknown, AnswerWording>([['everyday', 'auto']]);

/** Whether the onboarding invitation was skipped or completed by a save. */
export type AnswerStyleInvite = 'dismissed' | 'completed';

/** Version stamped on the locally stored guest record. */
export const ANSWER_STYLE_SCHEMA_VERSION = 1;

/**
 * Storage keys. `vm_`-prefixed like `vm_nav_mode` and `vm_language`: these are
 * the device's own guest preference, so they survive a signed-in refresh and a
 * sign-out. An account's choice is never written here — it lives in memory for
 * the signed-in session only (see `answerStyleStore.ts`).
 */
export const ANSWER_STYLE_STORAGE_KEY = 'vm_answer_style';
export const ANSWER_STYLE_INVITE_STORAGE_KEY = 'vm_answer_style_invite';

export const isAnswerWording = (value: unknown): value is AnswerWording =>
  ANSWER_WORDINGS.some((w) => w === value);
export const isAnswerLength = (value: unknown): value is AnswerLength =>
  ANSWER_LENGTHS.some((l) => l === value);

/** A `{wording, length}` pair with both halves recognized, or `null`. */
export function parseAnswerStyle(raw: unknown): AnswerStyle | null {
  if (!raw || typeof raw !== 'object') return null;
  const { wording: rawWording, length } = raw as { wording?: unknown; length?: unknown };
  const wording = LEGACY_WORDINGS.get(rawWording) ?? rawWording;
  return isAnswerWording(wording) && isAnswerLength(length) ? { wording, length } : null;
}

export function parseInviteState(raw: unknown): AnswerStyleInvite | null {
  return raw === 'dismissed' || raw === 'completed' ? raw : null;
}

export const sameAnswerStyle = (a: AnswerStyle, b: AnswerStyle): boolean =>
  a.wording === b.wording && a.length === b.length;

/** The guest record as stored: versioned so a future shape can be told apart. */
export function serializeGuestAnswerStyle(style: AnswerStyle, now: Date = new Date()): string {
  return JSON.stringify({
    version: ANSWER_STYLE_SCHEMA_VERSION,
    wording: style.wording,
    length: style.length,
    savedAt: now.toISOString(),
  });
}

/**
 * Read a stored guest record. Anything that does not parse — corrupt JSON, an
 * unknown value, a non-numeric version — reads as "never chosen" and is left in
 * place, so it is replaced only by the voter's next explicit save.
 */
export function parseGuestAnswerStyle(raw: string | null | undefined): AnswerStyle | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof (parsed as { version?: unknown }).version !== 'number') return null;
    return parseAnswerStyle(parsed);
  } catch {
    return null;
  }
}

/** i18n keys (namespace `common`), shared by the editor, Settings, and composer. */
export const wordingLabelKey = (w: AnswerWording) => `answerStyle.wording.${w}.label`;
export const wordingHintKey = (w: AnswerWording) => `answerStyle.wording.${w}.hint`;
export const lengthLabelKey = (l: AnswerLength) => `answerStyle.length.${l}.label`;
export const lengthHintKey = (l: AnswerLength) => `answerStyle.length.${l}.hint`;
/**
 * The editorially written sample answer for one combination. Nine fixed texts
 * answering the same neutral question with the same facts — rendered locally,
 * never generated — so the example shows the preference and nothing else.
 */
export const exampleAnswerKey = (style: AnswerStyle) =>
  `answerStyle.example.answers.${style.wording}.${style.length}`;

/**
 * The `answer_style` field for a chat request, or `undefined` to omit it.
 *
 * A signed-in session sends its style on every turn, the default pair
 * included: the request carries what the voter sees in this app right now, and
 * it outranks the account's saved value, which another device may have changed
 * since this one loaded it. The server renders nothing for the default pair, so
 * sending it changes no answer. A guest sends only a style that moves at least
 * one dimension off the default, so a default guest request — the partner
 * embed's included — stays byte-identical to one from before the feature.
 *
 * Omitted while the store has not finished loading, or while it still holds
 * the other identity's state (a signed-in session whose account has not
 * hydrated yet), so the server resolves from the saved account value instead of
 * a stale default.
 *
 * Older backends ignore unknown request fields, so sending it is safe there.
 */
export function answerStyleForRequest(
  state: { scope: 'guest' | 'account'; hydrated: boolean; style: AnswerStyle },
  { signedIn }: { signedIn: boolean }
): AnswerStyle | undefined {
  if (!state.hydrated) return undefined;
  if ((state.scope === 'account') !== signedIn) return undefined;
  if (state.scope === 'guest' && sameAnswerStyle(state.style, DEFAULT_ANSWER_STYLE)) {
    return undefined;
  }
  return { wording: state.style.wording, length: state.style.length };
}
